import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useBusinessType } from '@/hooks/useBusinessType';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Bell, MessageSquare, Mail, Phone,
  CheckCircle, XCircle, Eye, EyeOff, ExternalLink, AlertCircle,
  HelpCircle, Shield, Zap, RefreshCw, Unlink, Link2, Key,
  Tag, Kanban, FileText, Plug, Building2, Upload, Camera, Loader2, Star, CalendarCheck, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';

import AdminPage from '@/pages/AdminPage';
import SecuritySettings from '@/components/SecuritySettings';
import TagsManager from '@/components/TagsManager';
import PipelineConfigPage from '@/pages/PipelineConfigPage';
import CustomFieldsPage from '@/pages/CustomFieldsPage';
import IntegrationsPage from '@/pages/IntegrationsPage';

import { API_URL } from '@/config/api';

/* ─── General / Notifications ────────────────────────────────────────────── */
const GeneralSettings = () => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState({
    sms_enabled: true, email_enabled: true, phone_number: '', notification_email: ''
  });

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/settings/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setNotifications(prev => ({ ...prev, ...data }));
        }
      } catch (e) { console.error('Error fetching notification settings:', e); }
    })();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/settings/notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(notifications)
      });
      if (res.ok) toast.success('Notification settings saved');
      else toast.error('Failed to save notification settings');
    } catch { toast.error('Failed to save notification settings'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500" />
          Notification Preferences
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Choose how you want to be notified about new leads and follow-ups
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0 sm:pt-0">
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-lg gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm sm:text-base">SMS Notifications</p>
                <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Receive SMS alerts for new leads</p>
              </div>
            </div>
            <Switch
              checked={notifications.sms_enabled}
              onCheckedChange={(checked) => setNotifications({ ...notifications, sms_enabled: checked })}
            />
          </div>
          {notifications.sms_enabled && (
            <div className="ml-0 sm:ml-12">
              <Label htmlFor="phone" className="text-sm">Phone Number for SMS</Label>
              <div className="relative mt-1.5">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="phone" placeholder="+44 7700 900000" className="pl-10 min-h-[44px]"
                  value={notifications.phone_number || ''}
                  onChange={(e) => setNotifications({ ...notifications, phone_number: e.target.value })}
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-lg gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm sm:text-base">Email Notifications</p>
                <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Receive email alerts for new leads</p>
              </div>
            </div>
            <Switch
              checked={notifications.email_enabled}
              onCheckedChange={(checked) => setNotifications({ ...notifications, email_enabled: checked })}
            />
          </div>
          {notifications.email_enabled && (
            <div className="ml-0 sm:ml-12">
              <Label htmlFor="email" className="text-sm">Notification Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="email" type="email" placeholder="your@email.com" className="pl-10 min-h-[44px]"
                  value={notifications.notification_email || ''}
                  onChange={(e) => setNotifications({ ...notifications, notification_email: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>
        <Button onClick={handleSave} disabled={loading}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 w-full sm:w-auto min-h-[44px]"
          data-testid="save-notification-settings-btn"
        >
          Save Notification Settings
        </Button>
      </CardContent>
    </Card>
  );
};

/* ─── Business Info ─────────────────────────────────────────────────────── */
const BusinessInfoSettings = () => {
  const { user, selectedBusiness, checkAuth } = useAuth();
  const { isTrainingCentre } = useBusinessType();
  const businessId = selectedBusiness?.business_id || user?.business_id;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [info, setInfo] = useState({
    name: '', description: '', address: '', phone: '', email: '', website: '', logo_url: '',
  });

  useEffect(() => {
    if (selectedBusiness) {
      setInfo({
        name: selectedBusiness.name || '',
        description: selectedBusiness.description || '',
        address: selectedBusiness.address || '',
        phone: selectedBusiness.phone || '',
        email: selectedBusiness.email || '',
        website: selectedBusiness.website || '',
        logo_url: selectedBusiness.logo_url || '',
      });
    }
  }, [selectedBusiness]);

  const handleSave = async () => {
    if (!info.name.trim()) { toast.error('Business name is required'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/businesses/${businessId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...info,
          business_type: selectedBusiness?.business_type || 'general',
          primary_color: selectedBusiness?.primary_color || '#00B4D8',
        }),
      });
      if (res.ok) {
        toast.success('Business info saved');
        if (checkAuth) checkAuth();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Failed to save business info'); }
    finally { setLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/training/logo-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setInfo(prev => ({ ...prev, logo_url: data.logo_url }));
        toast.success('Logo uploaded');
        if (checkAuth) checkAuth();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const accentColor = isTrainingCentre ? 'pink' : 'cyan';

  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Building2 className={`w-4 h-4 sm:w-5 sm:h-5 text-${accentColor}-500`} />
          Business Information
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Your company details — visible to your team
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 p-4 sm:p-6 pt-0 sm:pt-0">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div className={`w-20 h-20 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50 flex-shrink-0`}>
            {info.logo_url ? (
              <img
                src={info.logo_url.startsWith('/') ? `${API_URL}${info.logo_url}` : info.logo_url}
                alt="Logo"
                className="w-full h-full object-cover rounded-2xl"
              />
            ) : (
              <Camera className="w-8 h-8 text-slate-300" />
            )}
          </div>
          <div>
            <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition-colors ${
              isTrainingCentre
                ? 'bg-pink-50 text-pink-600 hover:bg-pink-100'
                : 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100'
            }`}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading...' : 'Upload Logo'}
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploading} />
            </label>
            <p className="text-xs text-slate-400 mt-1">PNG, JPEG, or WebP. Max 2MB.</p>
          </div>
        </div>

        {/* Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-sm">Business Name *</Label>
            <Input value={info.name} onChange={(e) => setInfo(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Your company name" className="mt-1.5 rounded-xl min-h-[44px]" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm">Description</Label>
            <Textarea value={info.description} onChange={(e) => setInfo(prev => ({ ...prev, description: e.target.value }))}
              placeholder="A short description of your business..." className="mt-1.5 rounded-xl min-h-[80px]" rows={3} />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm">Address</Label>
            <Input value={info.address} onChange={(e) => setInfo(prev => ({ ...prev, address: e.target.value }))}
              placeholder="123 High Street, London, SW1A 1AA" className="mt-1.5 rounded-xl min-h-[44px]" />
          </div>
          <div>
            <Label className="text-sm">Phone</Label>
            <Input value={info.phone} onChange={(e) => setInfo(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="07700 900000" className="mt-1.5 rounded-xl min-h-[44px]" />
          </div>
          <div>
            <Label className="text-sm">Email</Label>
            <Input type="email" value={info.email} onChange={(e) => setInfo(prev => ({ ...prev, email: e.target.value }))}
              placeholder="info@yourbusiness.com" className="mt-1.5 rounded-xl min-h-[44px]" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm">Website</Label>
            <Input value={info.website} onChange={(e) => setInfo(prev => ({ ...prev, website: e.target.value }))}
              placeholder="https://yourbusiness.com" className="mt-1.5 rounded-xl min-h-[44px]" />
          </div>
        </div>

        <Button onClick={handleSave} disabled={loading}
          className={`w-full sm:w-auto min-h-[44px] rounded-xl ${
            isTrainingCentre
              ? 'bg-gradient-to-r from-pink-400 to-rose-500'
              : 'bg-gradient-to-r from-cyan-500 to-blue-600'
          }`}>
          {loading ? 'Saving...' : 'Save Business Info'}
        </Button>
      </CardContent>
    </Card>
  );
};

/* ─── Registration Webhook Field ────────────────────────────────────────── */
const RegistrationCalendarField = () => {
  const { user, selectedBusiness, checkAuth } = useAuth();
  const businessId = selectedBusiness?.business_id || user?.business_id;
  const [calendarId, setCalendarId] = useState(selectedBusiness?.registration_calendar_id || '');
  const [calendars, setCalendars] = useState([]);
  const [loadingCals, setLoadingCals] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCalendarId(selectedBusiness?.registration_calendar_id || '');
  }, [selectedBusiness?.registration_calendar_id]);

  useEffect(() => {
    const fetchCalendars = async () => {
      setLoadingCals(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/training/ghl/calendars`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setCalendars(data.calendars || []);
        }
      } catch { /* GHL not connected */ }
      finally { setLoadingCals(false); }
    };
    fetchCalendars();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/businesses/${businessId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...selectedBusiness,
          registration_calendar_id: calendarId || null,
        }),
      });
      if (res.ok) {
        toast.success('Registration calendar saved');
        if (checkAuth) checkAuth();
      } else {
        toast.error('Failed to save calendar');
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="reg_calendar" className="text-sm">Registration Calendar</Label>
        {loadingCals ? (
          <div className="flex items-center gap-2 mt-1.5 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading calendars from GHL...
          </div>
        ) : calendars.length > 0 ? (
          <Select value={calendarId} onValueChange={setCalendarId}>
            <SelectTrigger className="mt-1.5 min-h-[44px]">
              <SelectValue placeholder="Select your registration calendar" />
            </SelectTrigger>
            <SelectContent>
              {calendars.map(cal => (
                <SelectItem key={cal.id} value={cal.id}>
                  {cal.name || cal.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <>
            <Input
              id="reg_calendar"
              placeholder="Paste calendar ID from GHL"
              className="mt-1.5 font-mono text-xs sm:text-sm min-h-[44px]"
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1">Connect GHL first to see your calendars, or paste the calendar ID directly</p>
          </>
        )}
      </div>
      <Button onClick={handleSave} disabled={saving}
        className="bg-gradient-to-r from-pink-400 to-rose-500 min-h-[44px]">
        {saving ? 'Saving...' : 'Save Calendar'}
      </Button>
    </div>
  );
};

const ReviewWebhookField = () => {
  const { user, selectedBusiness, checkAuth } = useAuth();
  const businessId = selectedBusiness?.business_id || user?.business_id;
  const [webhookUrl, setWebhookUrl] = useState(selectedBusiness?.google_review_webhook_url || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setWebhookUrl(selectedBusiness?.google_review_webhook_url || '');
  }, [selectedBusiness?.google_review_webhook_url]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/businesses/${businessId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...selectedBusiness,
          google_review_webhook_url: webhookUrl.trim() || null,
        }),
      });
      if (res.ok) {
        toast.success('Review webhook URL saved');
        if (checkAuth) checkAuth();
      } else {
        toast.error('Failed to save webhook URL');
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="review_webhook" className="text-sm">GHL Workflow Webhook URL</Label>
        <Input
          id="review_webhook"
          placeholder="https://services.leadconnectorhq.com/hooks/..."
          className="mt-1.5 font-mono text-xs sm:text-sm min-h-[44px]"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
        />
        <p className="text-xs text-slate-500 mt-1">Paste the webhook URL from your &quot;Google Review Request&quot; workflow in GHL</p>
      </div>
      <Button onClick={handleSave} disabled={saving}
        className="bg-gradient-to-r from-pink-400 to-rose-500 min-h-[44px]">
        {saving ? 'Saving...' : 'Save Webhook URL'}
      </Button>
    </div>
  );
};

/* ─── GHL Integration ────────────────────────────────────────────────────── */
const GHLSettings = () => {
  const { user, selectedBusiness } = useAuth();
  const { isTrainingCentre } = useBusinessType();
  const businessId = selectedBusiness?.business_id || user?.business_id;
  const [ghlCredentials, setGhlCredentials] = useState({ private_integration_token: '', location_id: '', webhook_signing_secret: '' });
  const [ghlStatus, setGhlStatus] = useState(null);
  const [ghlLoading, setGhlLoading] = useState(false);
  const [showGhlToken, setShowGhlToken] = useState(false);

  const fetchGhlCredentials = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ghl/credentials`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); if (data) setGhlStatus(data); }
    } catch (e) { console.error('Error fetching GHL credentials:', e); }
  }, []);

  useEffect(() => { if (businessId) fetchGhlCredentials(); }, [businessId, fetchGhlCredentials]);

  const handleSaveGhlCredentials = async () => {
    if (!ghlCredentials.private_integration_token || !ghlCredentials.location_id) {
      toast.error('Please enter both the Private Integration Token and Location ID'); return;
    }
    setGhlLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ghl/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(ghlCredentials)
      });
      if (res.ok) {
        setGhlStatus(await res.json());
        toast.success('GHL credentials saved and connected!');
        setGhlCredentials(prev => ({ ...prev, private_integration_token: '' }));
      } else { const err = await res.json().catch(() => null); toast.error(err?.detail || 'Failed to connect to GHL — check your token and location ID'); }
    } catch (e) { console.error('GHL save error:', e); toast.error('Network error — please try again'); }
    finally { setGhlLoading(false); }
  };

  const handleTestGhlConnection = async () => {
    setGhlLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ghl/test-connection`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) { toast.success(`Connected to ${data.location_name || 'GHL'}!`); fetchGhlCredentials(); }
      else toast.error(data.error || 'Connection test failed');
    } catch { toast.error('Connection test failed'); }
    finally { setGhlLoading(false); }
  };

  const handleDisconnectGhl = async () => {
    if (!window.confirm('Disconnect GHL? This will stop syncing leads and conversations.')) return;
    setGhlLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/ghl/credentials`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      setGhlStatus(null); toast.success('GHL disconnected');
    } catch { toast.error('Failed to disconnect GHL'); }
    finally { setGhlLoading(false); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                GoHighLevel Integration
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {isTrainingCentre
                  ? 'Connect to GoHighLevel to sync registrations and automate workflows'
                  : 'Connect to GoHighLevel for lead sync, conversations, and automation'}
              </CardDescription>
            </div>
            <Badge className={ghlStatus?.is_connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
              {ghlStatus?.is_connected
                ? <><CheckCircle className="w-3 h-3 mr-1" /> Connected</>
                : <><XCircle className="w-3 h-3 mr-1" /> Not Connected</>}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
          {isTrainingCentre ? (
            <Alert className="bg-blue-50 border-blue-200">
              <HelpCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <AlertDescription className="text-blue-800 text-xs sm:text-sm">
                <strong>How to connect:</strong>
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>Log into GoHighLevel &rarr; Settings &rarr; Private Integrations</li>
                  <li>Click &quot;Create New Integration&quot; and name it &quot;Sarum Digital&quot;</li>
                  <li>Select scopes: <strong>contacts.readonly, contacts.write, calendars.readonly, calendars/events.readonly, calendars/events.write, locations.readonly</strong></li>
                  <li>Copy the token immediately (you can only view it once!)</li>
                  <li>Get your Location ID from Settings &rarr; Business Info</li>
                </ol>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="bg-blue-50 border-blue-200">
              <HelpCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <AlertDescription className="text-blue-800 text-xs sm:text-sm">
                <strong>How to get your GHL credentials:</strong>
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>Log into GoHighLevel &rarr; Settings &rarr; Private Integrations</li>
                  <li>Click &quot;Create New Integration&quot; and name it &quot;Sarum Digital&quot;</li>
                  <li>Select scopes: contacts.readonly, contacts.write, conversations.readonly, conversations.write, conversations/message.readonly, conversations/message.write, opportunities.readonly, opportunities.write, locations.readonly, locations/customFields.readonly, locations/customValues.readonly, locations/customValues.write, locations/tags.readonly, locations/tags.write, calendars.readonly, calendars/events.readonly, users.readonly</li>
                  <li>Copy the token immediately (you can only view it once!)</li>
                  <li>Get your Location ID from Settings &rarr; Business Info</li>
                </ol>
                <p className="mt-2 text-xs font-medium text-blue-900">Note: If you change scopes, GHL will regenerate your token. Copy the new token and re-enter it here.</p>
              </AlertDescription>
            </Alert>
          )}

          {ghlStatus?.is_connected && (
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <AlertDescription className="text-emerald-800 text-xs sm:text-sm">
                <strong>Connected to GHL</strong>
                <div className="mt-1 text-xs">
                  <p>Location ID: {ghlStatus.location_id}</p>
                  {ghlStatus.last_sync_at && <p>Last sync: {new Date(ghlStatus.last_sync_at).toLocaleString()}</p>}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {ghlStatus?.connection_error && (
            <Alert className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <AlertDescription className="text-red-800 text-xs sm:text-sm">
                <strong>Connection Error:</strong> {ghlStatus.connection_error}
              </AlertDescription>
            </Alert>
          )}

          {/* Only show webhook URL for general businesses — training uses GHL calendar triggers instead */}
          {!isTrainingCentre && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium">GHL Webhook URL</Label>
              <p className="text-xs text-slate-500 mb-2">Configure this URL in GHL to receive events</p>
              <div className="flex gap-2">
                <Input value={`${API_URL}/api/webhook/ghl`} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="sm"
                  onClick={() => { navigator.clipboard.writeText(`${API_URL}/api/webhook/ghl`); toast.success('Webhook URL copied!'); }}>
                  Copy
                </Button>
              </div>
            </div>
          )}

          {!ghlStatus?.is_connected && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label htmlFor="ghl_token" className="text-sm">Private Integration Token</Label>
                <div className="relative mt-1.5">
                  <Input id="ghl_token" type={showGhlToken ? 'text' : 'password'}
                    placeholder="pit_xxxxxxxxxxxxxxxxxx" className="pr-10 font-mono text-xs sm:text-sm min-h-[44px]"
                    value={ghlCredentials.private_integration_token}
                    onChange={(e) => setGhlCredentials({ ...ghlCredentials, private_integration_token: e.target.value })}
                  />
                  <button type="button" onClick={() => setShowGhlToken(!showGhlToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1">
                    {showGhlToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="ghl_location" className="text-sm">Location ID</Label>
                <Input id="ghl_location" placeholder="xxxxxxxxxxxxxxxxxx"
                  className="mt-1.5 font-mono text-xs sm:text-sm min-h-[44px]"
                  value={ghlCredentials.location_id}
                  onChange={(e) => setGhlCredentials({ ...ghlCredentials, location_id: e.target.value })}
                />
                <p className="text-xs text-slate-500 mt-1">Found in GHL Settings &rarr; Business Info or in the URL</p>
              </div>
              {/* Only show webhook signing secret for general businesses */}
              {!isTrainingCentre && (
                <div>
                  <Label htmlFor="ghl_secret" className="text-sm">Webhook Signing Secret (Optional)</Label>
                  <Input id="ghl_secret" type="password" placeholder="Optional - for webhook verification"
                    className="mt-1.5 font-mono text-xs sm:text-sm min-h-[44px]"
                    value={ghlCredentials.webhook_signing_secret}
                    onChange={(e) => setGhlCredentials({ ...ghlCredentials, webhook_signing_secret: e.target.value })}
                  />
                </div>
              )}
              <Button onClick={handleSaveGhlCredentials} disabled={ghlLoading}
                className={`w-full sm:w-auto min-h-[44px] ${isTrainingCentre ? 'bg-gradient-to-r from-pink-400 to-rose-500' : 'bg-gradient-to-r from-orange-500 to-amber-600'}`}
                data-testid="connect-ghl-btn">
                <Link2 className="w-4 h-4 mr-2" />
                {ghlLoading ? 'Connecting...' : 'Connect to GHL'}
              </Button>
            </div>
          )}

          {ghlStatus?.is_connected && (
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              <Button variant="outline" onClick={handleTestGhlConnection} disabled={ghlLoading} data-testid="test-ghl-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${ghlLoading ? 'animate-spin' : ''}`} /> Test Connection
              </Button>
              <Button variant="outline" onClick={handleDisconnectGhl} disabled={ghlLoading}
                className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="disconnect-ghl-btn">
                <Unlink className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registration Calendar — training centres only */}
      {isTrainingCentre && (
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CalendarCheck className="w-4 h-4 sm:w-5 sm:h-5 text-pink-500" />
              Registration Calendar
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              When you register a new student, they get automatically booked onto this GHL calendar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
            <Alert className="bg-blue-50 border-blue-200">
              <HelpCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <AlertDescription className="text-blue-800 text-xs sm:text-sm">
                <strong>How it works:</strong>
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>Select your registration calendar below</li>
                  <li>When you add a student as &quot;New Registration&quot;, they get booked on this calendar</li>
                  <li>GHL&apos;s &quot;Customer Booked Appointment&quot; trigger fires your reminder workflow automatically</li>
                </ol>
              </AlertDescription>
            </Alert>
            <RegistrationCalendarField />
          </CardContent>
        </Card>
      )}

      {/* Google Review Webhook — training centres only */}
      {isTrainingCentre && (
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
              Google Review Webhook
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              When you request a review after course completion, send student details to a GHL workflow
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
            <Alert className="bg-blue-50 border-blue-200">
              <HelpCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <AlertDescription className="text-blue-800 text-xs sm:text-sm">
                <strong>How to set up:</strong>
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>In GHL, create a Workflow with trigger &quot;Inbound Webhook&quot;</li>
                  <li>Copy the webhook URL from the workflow trigger</li>
                  <li>Paste it below — sends: name, phone, email, course name &amp; completion date</li>
                </ol>
              </AlertDescription>
            </Alert>
            <ReviewWebhookField />
          </CardContent>
        </Card>
      )}

      {/* What GHL provides — general businesses only */}
      {!isTrainingCentre && (
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg">What GHL Integration Provides</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { title: 'Real-time Lead Sync', desc: 'Leads from GHL appear instantly in your dashboard' },
                { title: 'Conversation Inbox', desc: 'See all messages and reply from your dashboard' },
                { title: 'AI/Human Ownership', desc: 'Take over from AI or return leads to automation' },
                { title: 'Pipeline Sync', desc: 'Stage changes sync both ways between apps' },
              ].map(f => (
                <div key={f.title} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div><p className="font-medium text-sm">{f.title}</p><p className="text-xs text-slate-500">{f.desc}</p></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─── SMS / Email (Twilio + SendGrid) ────────────────────────────────────── */
const SMSEmailSettings = () => {
  const { user, selectedBusiness } = useAuth();
  const businessId = selectedBusiness?.business_id || user?.business_id;
  const [loading, setLoading] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [showSendGridKey, setShowSendGridKey] = useState(false);
  const [credentials, setCredentials] = useState({
    twilio_account_sid: '', twilio_auth_token: '', twilio_phone_number: '',
    sendgrid_api_key: '', sendgrid_sender_email: ''
  });
  const [credStatus, setCredStatus] = useState({
    has_twilio: false, has_sendgrid: false,
    twilio_account_sid_configured: false, twilio_auth_token_configured: false,
    sendgrid_api_key_configured: false,
  });

  const fetchCredentials = useCallback(async () => {
    if (!businessId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/business/${businessId}/credentials`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setCredStatus({
          has_twilio: data.has_twilio, has_sendgrid: data.has_sendgrid,
          twilio_account_sid_configured: data.twilio_account_sid_configured,
          twilio_auth_token_configured: data.twilio_auth_token_configured,
          sendgrid_api_key_configured: data.sendgrid_api_key_configured,
        });
        if (data.twilio_phone_number) setCredentials(prev => ({ ...prev, twilio_phone_number: data.twilio_phone_number }));
        if (data.sendgrid_sender_email) setCredentials(prev => ({ ...prev, sendgrid_sender_email: data.sendgrid_sender_email }));
      }
    } catch (e) { console.error('Error fetching credentials:', e); }
  }, [businessId]);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/business/${businessId}/credentials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(credentials)
      });
      if (res.ok) {
        toast.success('Credentials saved successfully');
        fetchCredentials();
        setCredentials(prev => ({ ...prev, twilio_auth_token: '', sendgrid_api_key: '' }));
      } else { const err = await res.json(); toast.error(err.detail || 'Failed to save credentials'); }
    } catch { toast.error('Failed to save credentials'); }
    finally { setLoading(false); }
  };

  const twilioMissing = [
    !(credStatus.twilio_account_sid_configured || credentials.twilio_account_sid?.trim()) ? 'Account SID' : null,
    !(credStatus.twilio_auth_token_configured || credentials.twilio_auth_token?.trim()) ? 'Auth Token' : null,
    !credentials.twilio_phone_number?.trim() ? 'Twilio Phone Number' : null,
  ].filter(Boolean);
  const sendgridMissing = [
    !(credStatus.sendgrid_api_key_configured || credentials.sendgrid_api_key?.trim()) ? 'API Key' : null,
    !credentials.sendgrid_sender_email?.trim() ? 'Sender Email' : null,
  ].filter(Boolean);
  const twilioReady = twilioMissing.length === 0;
  const sendgridReady = sendgridMissing.length === 0;

  return (
    <div className="space-y-6">
      {/* Twilio SMS */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" /> Twilio SMS
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Send SMS notifications to leads and receive alerts</CardDescription>
            </div>
            <Badge className={twilioReady ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}>
              {twilioReady
                ? <><CheckCircle className="w-3 h-3 mr-1" /> Ready</>
                : <><AlertCircle className="w-3 h-3 mr-1" /> Incomplete</>}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
          <Alert className="bg-blue-50 border-blue-200">
            <HelpCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <AlertDescription className="text-blue-800 text-xs sm:text-sm">
              Get your Twilio credentials from{' '}
              <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                console.twilio.com <ExternalLink className="w-3 h-3 inline" />
              </a>
            </AlertDescription>
          </Alert>
          {!twilioReady && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <AlertDescription className="text-amber-800 text-xs sm:text-sm">
                Missing: {twilioMissing.join(', ')}
              </AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="twilio_sid" className="text-sm">Account SID</Label>
              <Input id="twilio_sid" placeholder="ACxxxxxxxx..." className="mt-1.5 font-mono text-xs sm:text-sm min-h-[44px]"
                value={credentials.twilio_account_sid}
                onChange={(e) => setCredentials({ ...credentials, twilio_account_sid: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="twilio_token" className="text-sm">Auth Token</Label>
              <div className="relative mt-1.5">
                <Input id="twilio_token" type={showTwilioToken ? 'text' : 'password'}
                  placeholder={credStatus.has_twilio ? '••••••••••••••••' : 'Enter auth token'}
                  className="pr-10 font-mono text-xs sm:text-sm min-h-[44px]"
                  value={credentials.twilio_auth_token}
                  onChange={(e) => setCredentials({ ...credentials, twilio_auth_token: e.target.value })} />
                <button type="button" onClick={() => setShowTwilioToken(!showTwilioToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1">
                  {showTwilioToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="twilio_phone" className="text-sm">Twilio Phone Number</Label>
            <Input id="twilio_phone" placeholder="+1234567890" className="mt-1.5 min-h-[44px]"
              value={credentials.twilio_phone_number}
              onChange={(e) => setCredentials({ ...credentials, twilio_phone_number: e.target.value })} />
            <p className="text-xs text-slate-500 mt-1">The phone number you purchased from Twilio</p>
          </div>
        </CardContent>
      </Card>

      {/* SendGrid Email */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" /> SendGrid Email
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Send email notifications and follow-up reminders</CardDescription>
            </div>
            <Badge className={sendgridReady ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}>
              {sendgridReady
                ? <><CheckCircle className="w-3 h-3 mr-1" /> Ready</>
                : <><AlertCircle className="w-3 h-3 mr-1" /> Incomplete</>}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
          <Alert className="bg-blue-50 border-blue-200">
            <HelpCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <AlertDescription className="text-blue-800 text-xs sm:text-sm">
              Get your SendGrid API key from{' '}
              <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                app.sendgrid.com <ExternalLink className="w-3 h-3 inline" />
              </a>
            </AlertDescription>
          </Alert>
          {!sendgridReady && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <AlertDescription className="text-amber-800 text-xs sm:text-sm">Missing: {sendgridMissing.join(', ')}</AlertDescription>
            </Alert>
          )}
          <div>
            <Label htmlFor="sendgrid_key" className="text-sm">API Key</Label>
            <div className="relative mt-1.5">
              <Input id="sendgrid_key" type={showSendGridKey ? 'text' : 'password'}
                placeholder={credStatus.has_sendgrid ? '••••••••••••••••' : 'SG.xxxxxxxxxxxxxxxx'}
                className="pr-10 font-mono text-xs sm:text-sm min-h-[44px]"
                value={credentials.sendgrid_api_key}
                onChange={(e) => setCredentials({ ...credentials, sendgrid_api_key: e.target.value })} />
              <button type="button" onClick={() => setShowSendGridKey(!showSendGridKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1">
                {showSendGridKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="sendgrid_email" className="text-sm">Sender Email</Label>
            <Input id="sendgrid_email" type="email" placeholder="notifications@yourdomain.com" className="mt-1.5 min-h-[44px]"
              value={credentials.sendgrid_sender_email}
              onChange={(e) => setCredentials({ ...credentials, sendgrid_sender_email: e.target.value })} />
            <p className="text-xs text-slate-500 mt-1">Must be a verified sender in SendGrid</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={loading}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 min-h-[44px]"
          data-testid="save-credentials-btn">
          <Shield className="w-4 h-4 mr-2" /> Save All Credentials
        </Button>
      </div>
    </div>
  );
};

/* ─── Settings Hub (Main Page) ───────────────────────────────────────────── */
const GENERAL_SECTIONS = [
  { id: 'business-info', label: 'Business Info', icon: Building2, description: 'Company details & logo' },
  { id: 'general', label: 'Notifications', icon: Bell, description: 'Notifications & preferences' },
  { id: 'ghl', label: 'GHL', icon: Zap, description: 'GoHighLevel integration', adminOnly: true },
  { id: 'sms-email', label: 'SMS / Email', icon: Key, description: 'Twilio & SendGrid', adminOnly: true },
  { id: 'integrations', label: 'Integrations', icon: Plug, description: 'Facebook, webhooks & tracking', adminOnly: true },
  { id: 'admin', label: 'Admin', icon: Shield, description: 'Users, businesses & campaigns', adminOnly: true },
  { id: 'tags', label: 'Tags', icon: Tag, description: 'Manage lead tags' },
  { id: 'pipelines', label: 'Pipelines', icon: Kanban, description: 'Pipeline & stage config' },
  { id: 'custom-fields', label: 'Custom Fields', icon: FileText, description: 'Custom field definitions' },
  { id: 'security', label: 'Password & security', icon: KeyRound, description: 'Change password, sign out everywhere' },
];

const TRAINING_SECTIONS = [
  { id: 'business-info', label: 'Business Info', icon: Building2, description: 'Company details & logo' },
  { id: 'ghl', label: 'GHL', icon: Zap, description: 'GoHighLevel integration', superAdminOnly: true },
  { id: 'admin', label: 'Admin', icon: Shield, description: 'Users & businesses', superAdminOnly: true },
  { id: 'security', label: 'Password & security', icon: KeyRound, description: 'Change password, sign out everywhere' },
];

const SettingsPage = () => {
  const { user } = useAuth();
  const { isTrainingCentre } = useBusinessType();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'business-info';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const allSections = isTrainingCentre ? TRAINING_SECTIONS : GENERAL_SECTIONS;
  const isSuperAdmin = user?.role === 'super_admin';
  const visibleSections = allSections.filter(s => {
    if (s.superAdminOnly) return isSuperAdmin;
    if (s.adminOnly) return isAdmin;
    return true;
  });

  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  const renderContent = () => {
    switch (activeTab) {
      case 'business-info': return <BusinessInfoSettings />;
      case 'general': return <GeneralSettings />;
      case 'ghl': return isAdmin ? <GHLSettings /> : null;
      case 'sms-email': return isAdmin ? <SMSEmailSettings /> : null;
      case 'integrations': return isAdmin ? <IntegrationsPage /> : null;
      case 'admin': return isAdmin ? <AdminPage /> : null;
      case 'tags': return <TagsManager />;
      case 'pipelines': return <PipelineConfigPage />;
      case 'custom-fields': return <CustomFieldsPage />;
      case 'security': return <SecuritySettings />;
      default: return <BusinessInfoSettings />;
    }
  };

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">Manage your workspace configuration</p>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block w-56 flex-shrink-0">
          <nav className="sticky top-4 space-y-0.5">
            {visibleSections.map(section => {
              const Icon = section.icon;
              const isActive = activeTab === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveTab(section.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150",
                    isActive
                      ? isTrainingCentre
                        ? "bg-pink-50 text-pink-700 font-medium shadow-sm"
                        : "bg-cyan-50 text-cyan-700 font-medium shadow-sm"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  )}
                  data-testid={`settings-tab-${section.id}`}
                >
                  <Icon className={cn("w-4 h-4 flex-shrink-0", isActive ? (isTrainingCentre ? "text-pink-600" : "text-cyan-600") : "text-slate-400")} />
                  <span className="text-sm truncate">{section.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Mobile Tab Selector */}
        <div className="md:hidden">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full" data-testid="settings-mobile-tab-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {visibleSections.map(section => (
                <SelectItem key={section.id} value={section.id}>
                  {section.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
